import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "form", "subject", "body", "previewSubject", "previewBody", "previewSender",
    "promoPanel", "promoSetup", "promoResult", "promoEnabled", "promoAmount",
    "promoDays", "promoDaysHidden", "promoCode", "promoCodeText", "promoSummary",
    "previewPromo", "previewPromoAmount", "previewPromoCode", "previewPromoCopy", "sendButton", "draftButton",
    "ctaMode", "ctaLabel", "ctaEnabled", "ctaFields", "disabledCtaLabel", "ctaLink", "ctaLinkOption",
    "previewCta", "previewCtaLabel", "previewCtaSubtext"
  ]
  static values = {
    preview: Object,
    claimToken: String,
    claimUrl: String,
    promoPrefix: { type: String, default: "ECHO-BACK" },
    saveDateSubject: String,
    saveDateBody: String,
    partySubject: String,
    partyBody: String,
    saveDateLabel: String,
    partyLabel: String,
    ctaLinks: Object
  }

  connect() {
    document.body.classList.add("admin-marketing-overlay-open")
    this.promoAmountValue = this.hasPromoAmountTarget ? Number(this.promoAmountTarget.value) || 25 : 25
    if (this.hasPromoEnabledTarget && this.promoEnabledTarget.value === "1") this.restorePromo()
    this.syncCtaPresentation()
    if (this.hasSubjectTarget) this.preview()
    this.heartbeatClaim()
    this.claimTimer = window.setInterval(() => this.heartbeatClaim(), 60_000)
  }

  disconnect() {
    window.clearInterval(this.claimTimer)
    if (!this.submitInFlight) this.releaseClaim()
    if (!document.querySelector(".admin-marketing-composer-layer")) {
      document.body.classList.remove("admin-marketing-overlay-open")
    }
  }

  close(event) {
    event?.preventDefault()
    if (this.submitInFlight) return
    this.releaseClaim()
    const frame = this.element.closest("turbo-frame")
    if (frame) {
      frame.removeAttribute("src")
      frame.innerHTML = ""
    }
    document.body.classList.remove("admin-marketing-overlay-open")
  }

  senderChanged(event) {
    this.element.querySelectorAll(".admin-marketing-sender-card").forEach((card) => card.classList.remove("active"))
    event.currentTarget.closest(".admin-marketing-sender-card")?.classList.add("active")
    this.previewValue = { ...this.previewValue, sender_name: event.currentTarget.dataset.senderName }
    if (this.hasPreviewSenderTarget) this.previewSenderTarget.textContent = event.currentTarget.dataset.senderEmail
    this.preview()
  }

  insertField(event) {
    const field = event.currentTarget.dataset.field
    const target = this.bodyTarget
    const start = target.selectionStart ?? target.value.length
    const finish = target.selectionEnd ?? start
    target.setRangeText(field, start, finish, "end")
    target.focus()
    this.preview()
  }

  preview() {
    if (!this.hasSubjectTarget) return
    this.previewSubjectTarget.textContent = this.interpolate(this.subjectTarget.value)
    this.previewBodyTarget.textContent = this.interpolate(this.bodyTarget.value)
    if (this.hasCtaLabelTarget && this.hasPreviewCtaLabelTarget) {
      this.previewCtaLabelTarget.textContent = `${this.interpolate(this.ctaLabelTarget.value.trim())} →`
      if (this.hasDisabledCtaLabelTarget) this.disabledCtaLabelTarget.value = this.ctaLabelTarget.value
    }
  }

  promoAmountChanged(event) {
    this.promoAmountValue = Number(event.currentTarget.value)
    this.promoAmountTarget.value = this.promoAmountValue
    this.element.querySelectorAll(".admin-marketing-promo-amounts label").forEach((label) => label.classList.remove("active"))
    event.currentTarget.closest("label")?.classList.add("active")
  }

  selectCtaMode(event) {
    if (!this.hasCtaModeTarget || (this.hasCtaEnabledTarget && !this.ctaEnabledTarget.checked)) return

    const previousMode = this.ctaModeTarget.value || "save_date"
    const nextMode = event.currentTarget.dataset.ctaMode
    if (!["save_date", "party", "how_it_works"].includes(nextMode)) return
    const previousDefaults = this.ctaDefaults(previousMode)
    const nextDefaults = this.ctaDefaults(nextMode)

    this.ctaModeTarget.value = nextMode
    if (this.hasCtaLabelTarget && this.ctaLabelTarget.value.trim() === previousDefaults.label) {
      this.ctaLabelTarget.value = nextDefaults.label
    }
    if (this.hasSubjectTarget && this.subjectTarget.value.trim() === previousDefaults.subject.trim()) {
      this.subjectTarget.value = nextDefaults.subject
    }
    if (this.hasBodyTarget && this.bodyTarget.value.trim() === previousDefaults.body.trim()) {
      this.bodyTarget.value = nextDefaults.body
    }

    this.syncCtaPresentation()
    this.refreshPromoPreviewCopy()
    this.preview()
  }

  ctaEnabledChanged() {
    this.syncCtaPresentation()
    this.preview()
  }

  generatePromo() {
    const days = Math.max(1, Math.min(365, Number(this.promoDaysTarget.value) || 30))
    const code = `${this.promoPrefixValue}-${this.randomSuffix(7)}`

    this.promoEnabledTarget.value = "1"
    this.promoAmountTarget.value = this.promoAmountValue
    this.promoDaysHiddenTarget.value = days
    this.promoCodeTarget.value = code
    this.restorePromo()
  }

  restorePromo() {
    const code = this.promoCodeTarget.value
    const days = Math.max(1, Math.min(365, Number(this.promoDaysHiddenTarget.value) || 30))
    const expires = new Date(Date.now() + days * 86_400_000)
    const expiryLabel = expires.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })
    this.promoCodeTextTarget.textContent = code
    this.promoSummaryTarget.textContent = `Adds ${this.money(this.promoAmountValue)} to their party total · split between their gift and chosen charity · valid ${days} days · expires ${expiryLabel}`
    this.promoSetupTarget.hidden = true
    this.promoResultTarget.hidden = false
    this.promoPanelTarget.classList.add("is-generated")

    this.previewPromoCodeTarget.textContent = code
    this.promoExpiryLabel = expiryLabel
    this.refreshPromoPreviewCopy()
    this.previewPromoTarget.hidden = false
  }

  removePromo() {
    this.promoEnabledTarget.value = "0"
    this.promoCodeTarget.value = ""
    this.promoSetupTarget.hidden = false
    this.promoResultTarget.hidden = true
    this.previewPromoTarget.hidden = true
    this.promoPanelTarget.classList.remove("is-generated")
  }

  sending() {
    if (!this.hasSendButtonTarget) return
    this.submitInFlight = true
    this.sendButtonTarget.disabled = true
    this.sendButtonTarget.classList.add("is-loading")
    if (this.hasDraftButtonTarget) this.draftButtonTarget.disabled = true
  }

  sent(event) {
    if (event.detail.success) return
    this.submitInFlight = false
    if (!this.hasSendButtonTarget) return
    this.sendButtonTarget.disabled = false
    this.sendButtonTarget.classList.remove("is-loading")
    if (this.hasDraftButtonTarget) this.draftButtonTarget.disabled = false
  }

  interpolate(template) {
    return String(template).replace(/\{\{(first_name|child_name|charity|last_year|amount|est_age|sender_name)\}\}/g, (token, key) => {
      const value = String(this.previewValue[key] ?? token)
      return ["first_name", "child_name"].includes(key) ? value.replace(/^(\s*)(\S)/u, (_, space, letter) => space + letter.toUpperCase()) : value
    })
  }

  ctaDefaults(mode) {
    const label = this.ctaLinksValue?.[mode]?.label
    return mode === "save_date"
      ? { subject: this.saveDateSubjectValue || "", body: this.saveDateBodyValue || "", label: label || this.saveDateLabelValue }
      : { subject: this.partySubjectValue || "", body: this.partyBodyValue || "", label: label || this.partyLabelValue }
  }

  currentCtaMode() {
    return this.hasCtaModeTarget ? this.ctaModeTarget.value : "party"
  }

  syncCtaPresentation() {
    if (!this.hasCtaModeTarget) return

    const mode = this.currentCtaMode()
    const link = this.ctaLinksValue?.[mode]
    const enabled = !this.hasCtaEnabledTarget || this.ctaEnabledTarget.checked
    if (this.hasCtaFieldsTarget) this.ctaFieldsTarget.disabled = !enabled
    if (this.hasDisabledCtaLabelTarget) this.disabledCtaLabelTarget.disabled = enabled
    if (this.hasPreviewCtaTarget) this.previewCtaTarget.hidden = !enabled
    this.ctaLinkOptionTargets.forEach(option => option.setAttribute("aria-pressed", String(option.dataset.ctaMode === mode)))
    if (this.hasCtaLinkTarget && link) this.ctaLinkTarget.value = link.path
    if (this.hasPreviewCtaSubtextTarget && link) this.previewCtaSubtextTarget.textContent = link.subtext
  }

  refreshPromoPreviewCopy() {
    if (!this.hasPreviewPromoCopyTarget || !this.promoExpiryLabel) return

    const amount = this.money(this.promoAmountValue)
    if (this.hasPreviewPromoAmountTarget) this.previewPromoAmountTarget.textContent = amount
    const child = this.interpolate("{{child_name}}") === "{{child_name}}" ? "their child" : this.interpolate("{{child_name}}")
    this.previewPromoCopyTarget.textContent = this.currentCtaMode() === "save_date"
      ? `We'll reserve this bonus for ${child}'s first party. When they create it using this email address, the bonus is added automatically and split between their gift and chosen charity. Expires ${this.promoExpiryLabel}.`
      : `We'll add this bonus to ${child}'s party total. The bonus will be split between their gift and the charity selected for that party. Expires ${this.promoExpiryLabel}.`
  }

  randomSuffix(length) {
    const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"
    const values = new Uint32Array(length)
    window.crypto.getRandomValues(values)
    return Array.from(values, (value) => alphabet[value % alphabet.length]).join("")
  }

  money(value) {
    return Number(value).toLocaleString("en-CA", { style: "currency", currency: "CAD", minimumFractionDigits: 0, maximumFractionDigits: 2 })
  }

  heartbeatClaim() {
    if (!this.hasClaimUrlValue || !this.claimTokenValue) return
    fetch(this.claimUrlValue, {
      method: "PATCH",
      headers: this.csrfHeaders(),
      keepalive: true
    }).catch(() => {})
  }

  releaseClaim() {
    if (this.claimReleased || !this.hasClaimUrlValue || !this.claimTokenValue) return
    this.claimReleased = true
    fetch(this.claimUrlValue, {
      method: "DELETE",
      headers: this.csrfHeaders(),
      keepalive: true
    }).catch(() => {})
  }

  csrfHeaders() {
    return {
      Accept: "application/json",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
    }
  }
}
